import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Headphones, Pin, Instagram, ExternalLink, Plus, FileText, Sparkles, Clock, Check, Eye, EyeOff, RotateCcw } from "lucide-react";

// Each entry = one published audio essay with its social pack URLs
const ESSAYS = [
  {
    slug: "the-tap-in",
    title: "The HYGIENE That Reclaimed My Life",
    subtitle: "Tap-In. Ground. Say Hello. Say Thank You.",
    publishedDate: "Apr 28, 2026",
    duration: "9 min",
    status: "live",
    blog: "/blog/the-tap-in",
    pin: "/pin/the-tap-in",
    post: "/post/the-tap-in",
    audio: "/downloads/audio/the-tap-in.m4a",
    accent: "from-[#25a1c2] to-[#042F2E]",
  },
  {
    slug: "messy-middle-code-of-conduct",
    title: "The Messy Middle & Your Personal Code of Conduct",
    subtitle: "13 tests · build your armor BEFORE sh!t hits the fan",
    publishedDate: "Apr 23, 2026",
    duration: "12 min",
    status: "live",
    blog: "/blog/messy-middle-code-of-conduct",
    pin: null, // not built yet
    post: null,
    audio: "/downloads/audio/messy-middle.m4a",
    accent: "from-[#068fa2] to-[#042F2E]",
  },
  {
    slug: "toxic-positivity",
    title: "The Quiet Violence of Toxic Positivity",
    subtitle: "10 phrases · the HYGIENE practice to replace them",
    publishedDate: "Apr 22, 2026",
    duration: "9 min",
    status: "live",
    blog: "/blog/toxic-positivity",
    pin: null,
    post: null,
    audio: "/downloads/audio/toxic-positivity.m4a",
    accent: "from-[#042F2E] to-[#068fa2]",
  },
];

const QUICK_LINKS = [
  { label: "📊 Funnel Stats · live MailerLite", to: "/funnel-stats" },
  { label: "🎁 Tap-In Calendar (Reel lead magnet)", to: "/tap-in-calendar" },
  { label: "📧 Email Drafts · for ConvertKit", to: "/email-drafts" },
  { label: "🧠 Voice Pack · for Claude Cowork", to: "/voice-pack" },
  { label: "💬 Voice Prompt · for any AI tool", to: "/voice-prompt" },
  { label: "🎯 Voice Tester · grade AI outputs", to: "/cowork-test" },
  { label: "🪨 Content Bank · 50-post matrix", to: "/content-bank" },
  { label: "Studio · Recording template", to: "/studio" },
  { label: "Distribution dashboard", to: "/distribute" },
  { label: "AI Commands library", to: "/commands" },
  { label: "RSS feed (Buffer)", to: "/rss.xml", external: true },
  { label: "Yes. No. Wait. lead magnet", to: "/yes-no-wait" },
];

const ROADMAP_ITEMS = [
  {
    id: "factory",
    priority: "P1",
    title: "🏭 The Content Factory",
    desc: "Paste any topic → 5 archetype variants in your voice + brand images. ~30 min build.",
    trigger: "build the factory",
    color: "bg-[#25a1c2] text-white",
  },
  {
    id: "other-2-packs",
    priority: "P1",
    title: "📦 Social packs for Toxic Positivity + Messy Middle",
    desc: "Same Pin + IG/FB structure as Tap-In. Adds them to this hub.",
    trigger: "build the social packs for the other 2",
    color: "bg-[#25a1c2] text-white",
  },
  {
    id: "remaining-45",
    priority: "P1",
    title: "✍️ The remaining 45 posts",
    desc: "Once test week reveals the winner — I write the full 5 × 10 matrix.",
    trigger: "write the remaining 45",
    color: "bg-[#25a1c2] text-white",
  },
  {
    id: "pdf-generator",
    priority: "P2",
    title: "📄 One-click PDF for Yes. No. Wait. cards",
    desc: "Better than browser print-to-PDF. Cleaner email capture flow.",
    trigger: "build the PDF generator",
    color: "bg-[#475569] text-white",
  },
  {
    id: "analytics",
    priority: "P2",
    title: "📊 Insights dashboard",
    desc: "Real GA4 view counts on every essay card. Wait 5-7 days for data first.",
    trigger: "build the analytics layer",
    color: "bg-[#475569] text-white",
  },
  {
    id: "voice-prompt",
    priority: "P2",
    title: "🧠 Temple Voice System Prompt",
    desc: "Private page with your saved AI voice prompt + samples. One-click copy.",
    trigger: "build the voice prompt page",
    color: "bg-[#475569] text-white",
  },
  {
    id: "pillar-2",
    priority: "P2",
    title: "📚 Pillar 2 content pack: Yes. No. Wait.",
    desc: "5 more posts on the decision-making pillar.",
    trigger: "build pillar 2",
    color: "bg-[#475569] text-white",
  },
  {
    id: "pillar-3",
    priority: "P2",
    title: "🛡️ Pillar 3 content pack: Boundaries",
    desc: "Strongest sales pillar — drives Boundary Builder $14.",
    trigger: "build pillar 3",
    color: "bg-[#475569] text-white",
  },
];

const ROADMAP_STORAGE_KEY = "temple_roadmap_completed_v1";

const PlatformLink = ({ to, icon: Icon, label, sublabel, available, testId }) => {
  if (!available) {
    return (
      <div
        className="flex items-center gap-3 p-3 rounded-xl border border-dashed border-[#CCFBF1] bg-[#efeae7] opacity-50"
        data-testid={testId + "-pending"}
      >
        <Icon className="w-4 h-4 text-[#475569] flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold text-[#475569] truncate">{label}</p>
          <p className="text-[10px] text-[#475569] truncate">Not built yet · ask me to build it</p>
        </div>
      </div>
    );
  }
  return (
    <Link
      to={to}
      className="flex items-center gap-3 p-3 rounded-xl border border-[#CCFBF1] bg-white hover:bg-[#F0F5F4] hover:border-[#25a1c2] transition-all group"
      data-testid={testId}
    >
      <Icon className="w-4 h-4 text-[#25a1c2] flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold text-[#042F2E] truncate">{label}</p>
        <p className="text-[10px] text-[#475569] truncate">{sublabel}</p>
      </div>
      <ExternalLink className="w-3.5 h-3.5 text-[#25a1c2] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
    </Link>
  );
};

const Roadmap = () => {
  const [completed, setCompleted] = useState({});
  const [showCompleted, setShowCompleted] = useState(false);
  const [justCheckedId, setJustCheckedId] = useState(null);

  // Load saved state on mount
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(ROADMAP_STORAGE_KEY) || "{}");
      setCompleted(saved);
    } catch {
      setCompleted({});
    }
  }, []);

  const toggle = (id) => {
    const next = { ...completed, [id]: !completed[id] };
    if (!completed[id]) {
      // Just checked it → celebrate
      setJustCheckedId(id);
      setTimeout(() => setJustCheckedId(null), 1200);
    }
    setCompleted(next);
    localStorage.setItem(ROADMAP_STORAGE_KEY, JSON.stringify(next));
  };

  const resetAll = () => {
    if (window.confirm("Reset all checked items? They'll come back to the active queue.")) {
      setCompleted({});
      localStorage.removeItem(ROADMAP_STORAGE_KEY);
    }
  };

  const total = ROADMAP_ITEMS.length;
  const doneCount = ROADMAP_ITEMS.filter((i) => completed[i.id]).length;
  const remaining = total - doneCount;

  const visibleItems = ROADMAP_ITEMS.filter((i) => (showCompleted ? completed[i.id] : !completed[i.id]));

  return (
    <div className="bg-white rounded-2xl border border-[#CCFBF1] p-8 mb-12" data-testid="roadmap-card">
      <div className="flex items-start justify-between gap-4 mb-2 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#25a1c2] rounded-full flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-[#5EEAD4]" />
          </div>
          <div>
            <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#25a1c2] font-bold">
              Available · Just Say the Word
            </p>
            <h2 className="font-serif text-2xl text-[#042F2E]">What's Queued Next</h2>
          </div>
        </div>

        {/* Progress + toggle */}
        <div className="flex items-center gap-3">
          <div className="text-right" data-testid="roadmap-progress">
            <p className="font-serif text-3xl text-[#25a1c2] font-bold leading-none">
              {remaining}<span className="text-[#475569] text-lg">/{total}</span>
            </p>
            <p className="text-[10px] uppercase tracking-widest text-[#475569] font-bold">
              {doneCount === 0 ? "in queue" : `${doneCount} shipped 🎉`}
            </p>
          </div>
        </div>
      </div>

      <p className="text-[#475569] text-sm mb-4">
        Logged ideas you've approved. Tell me the trigger phrase any time → I'll build it. Check off when shipped. ✓
      </p>

      {/* Progress bar */}
      <div className="w-full bg-[#F0F5F4] rounded-full h-2 mb-6 overflow-hidden">
        <div
          className="bg-gradient-to-r from-[#25a1c2] to-[#5EEAD4] h-full transition-all duration-700 ease-out"
          style={{ width: `${(doneCount / total) * 100}%` }}
          data-testid="roadmap-progress-bar"
        ></div>
      </div>

      {/* View toggle */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setShowCompleted(!showCompleted)}
          className="inline-flex items-center gap-1.5 text-xs font-bold text-[#25a1c2] hover:text-[#042F2E] transition-colors"
          data-testid="toggle-completed-btn"
        >
          {showCompleted ? (
            <>
              <Eye className="w-3.5 h-3.5" /> Showing shipped — click for queue
            </>
          ) : (
            <>
              <EyeOff className="w-3.5 h-3.5" /> Showing queue — click for shipped ({doneCount})
            </>
          )}
        </button>
        {doneCount > 0 && (
          <button
            onClick={resetAll}
            className="inline-flex items-center gap-1.5 text-[10px] text-[#475569] hover:text-[#042F2E] transition-colors"
            data-testid="reset-roadmap-btn"
          >
            <RotateCcw className="w-3 h-3" /> Reset all
          </button>
        )}
      </div>

      <div className="space-y-3" data-testid="roadmap-items">
        {visibleItems.length === 0 ? (
          <div className="text-center py-12 text-[#475569]">
            {showCompleted ? (
              <>
                <p className="font-serif italic text-lg mb-2">Nothing shipped yet — but you're early. 💛</p>
                <p className="text-xs">Check off your first item to celebrate it here.</p>
              </>
            ) : (
              <>
                <p className="font-serif italic text-2xl text-[#25a1c2] mb-2">🎉 Queue empty. You shipped everything.</p>
                <p className="text-sm">Welcome home, builder. Time to dream up the next batch.</p>
              </>
            )}
          </div>
        ) : (
          visibleItems.map((item) => {
            const isDone = !!completed[item.id];
            const isCelebrating = justCheckedId === item.id;
            return (
              <div
                key={item.id}
                className={`flex items-start gap-3 p-4 rounded-xl border transition-all duration-300 ${
                  isDone
                    ? "bg-[#F0FDF4] border-[#25a1c2]/30 opacity-70"
                    : "bg-[#efeae7] border-[#CCFBF1] hover:border-[#25a1c2]"
                } ${isCelebrating ? "scale-[1.02] shadow-lg ring-2 ring-[#5EEAD4]" : ""}`}
                data-testid={`roadmap-item-${item.id}`}
              >
                {/* Checkbox */}
                <button
                  onClick={() => toggle(item.id)}
                  className={`flex-shrink-0 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${
                    isDone
                      ? "bg-[#25a1c2] border-[#25a1c2]"
                      : "bg-white border-[#25a1c2]/30 hover:border-[#25a1c2]"
                  }`}
                  aria-label={isDone ? "Mark as not done" : "Mark as done"}
                  data-testid={`checkbox-${item.id}`}
                >
                  {isDone && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
                </button>

                <span className={`flex-shrink-0 ${item.color} text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full ${isDone ? "opacity-60" : ""}`}>
                  {item.priority}
                </span>

                <div className="flex-1 min-w-0">
                  <h3 className={`font-sans font-bold text-sm ${isDone ? "text-[#475569] line-through" : "text-[#042F2E]"}`}>
                    {item.title}
                  </h3>
                  <p className={`text-xs leading-relaxed mb-2 ${isDone ? "text-[#475569]/60 line-through" : "text-[#475569]"}`}>
                    {item.desc}
                  </p>
                  {!isDone && (
                    <p className="text-[#25a1c2] text-[11px] font-mono">
                      Trigger: <span className="bg-[#F0F5F4] px-2 py-0.5 rounded italic">"{item.trigger}"</span>
                    </p>
                  )}
                  {isDone && isCelebrating && (
                    <p className="text-[#25a1c2] text-xs font-bold animate-pulse">
                      ✨ Shipped! Welcome home, builder. 💛
                    </p>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="mt-6 pt-5 border-t border-[#CCFBF1] flex items-center gap-2 text-[#475569] text-xs">
        <Clock className="w-3.5 h-3.5 flex-shrink-0" />
        <p className="italic">
          All ideas are logged in <span className="font-mono">/app/memory/ROADMAP.md</span> so I remember them across every chat session. Your check marks save in this browser.
        </p>
      </div>
    </div>
  );
};

export default function SocialHub() {  useEffect(() => {
    document.title = "Publish Toolkit — Temple Franklin";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  return (
    <div className="bg-[#efeae7] min-h-screen pb-16" data-testid="social-hub-page">
      <header className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-xl border-b border-[#CCFBF1]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="text-[#25a1c2] hover:text-[#042F2E] text-sm font-medium inline-flex items-center gap-1.5"
          >
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#25a1c2] bg-[#F0F5F4] px-2.5 py-1 rounded-full">
            Publish Toolkit · Private
          </span>
        </div>
      </header>

      <div className="pt-28 px-6">
        <div className="max-w-6xl mx-auto">
          {/* HEADER */}
          <div className="text-center mb-12">
            <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#25a1c2] font-bold mb-3">
              Your Internal Publishing Dashboard
            </p>
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-[#042F2E] mb-4">
              The Publish Toolkit
            </h1>
            <p className="text-[#475569] max-w-2xl mx-auto">
              Every essay. Every social pack. One click away. No more hunting for URLs.
            </p>
          </div>

          {/* ESSAYS LIST */}
          <div className="space-y-6 mb-16" data-testid="essays-list">
            {ESSAYS.map((essay) => (
              <div
                key={essay.slug}
                className="bg-white rounded-2xl border border-[#CCFBF1] overflow-hidden shadow-sm hover:shadow-md transition-all"
                data-testid={`essay-card-${essay.slug}`}
              >
                {/* TOP STRIPE */}
                <div className={`bg-gradient-to-r ${essay.accent} px-6 py-5`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="bg-[#5EEAD4]/20 text-[#5EEAD4] text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full">
                          Live
                        </span>
                        <span className="text-white/60 text-xs">
                          {essay.publishedDate} · {essay.duration} listen
                        </span>
                      </div>
                      <h2 className="font-serif text-xl md:text-2xl text-white leading-tight mb-1">
                        {essay.title}
                      </h2>
                      <p className="font-serif italic text-[#CCFBF1] text-sm">
                        {essay.subtitle}
                      </p>
                    </div>
                  </div>
                </div>

                {/* PLATFORM GRID */}
                <div className="p-6">
                  <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#25a1c2] font-bold mb-3">
                    Publish Anywhere · Click to Open
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <PlatformLink
                      to={essay.blog}
                      icon={Headphones}
                      label="Blog Post"
                      sublabel="Live audio + essay"
                      available={!!essay.blog}
                      testId={`${essay.slug}-blog`}
                    />
                    <PlatformLink
                      to={essay.pin}
                      icon={Pin}
                      label="Pinterest Pin"
                      sublabel="Image + copy blocks"
                      available={!!essay.pin}
                      testId={`${essay.slug}-pin`}
                    />
                    <PlatformLink
                      to={essay.post}
                      icon={Instagram}
                      label="Instagram + FB"
                      sublabel="Square · Story · captions"
                      available={!!essay.post}
                      testId={`${essay.slug}-post`}
                    />
                    <PlatformLink
                      to={essay.audio}
                      icon={FileText}
                      label="Audio File"
                      sublabel="Download / share"
                      available={!!essay.audio}
                      testId={`${essay.slug}-audio`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ADD-NEW PROMPT */}
          <div className="bg-gradient-to-br from-[#FEF3C7] via-[#FDE68A] to-[#FCD34D] rounded-2xl p-8 mb-12 text-center border-2 border-[#F59E0B]" data-testid="add-new-essay-card">
            <div className="w-12 h-12 bg-[#92400E] rounded-full flex items-center justify-center mx-auto mb-4">
              <Plus className="w-6 h-6 text-[#FDE68A]" />
            </div>
            <h2 className="font-serif text-2xl text-[#451A03] mb-2">Recorded a New Essay?</h2>
            <p className="text-[#78350F] text-sm max-w-md mx-auto mb-4">
              Send me the audio file and the title. I'll publish the blog post, build the Pinterest pin page, and the Instagram + Facebook pack — all linked here automatically.
            </p>
            <p className="text-[#92400E] text-xs italic">
              Average turnaround: 5 minutes. Real tools, for real Temple. 💛
            </p>
          </div>

          {/* ROADMAP — what's queued */}
          <Roadmap />

          {/* QUICK LINKS */}
          <div className="bg-white rounded-2xl border border-[#CCFBF1] p-6">
            <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#25a1c2] font-bold mb-4">
              Other Private Tools
            </p>
            <div className="grid sm:grid-cols-2 gap-3" data-testid="quick-links">
              {QUICK_LINKS.map((link) =>
                link.external ? (
                  <a
                    key={link.to}
                    href={link.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-3 rounded-xl bg-[#efeae7] hover:bg-[#F0F5F4] text-[#042F2E] text-sm transition-all group"
                  >
                    <ExternalLink className="w-3.5 h-3.5 text-[#25a1c2] flex-shrink-0" />
                    <span className="truncate">{link.label}</span>
                  </a>
                ) : (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="flex items-center gap-2 p-3 rounded-xl bg-[#efeae7] hover:bg-[#F0F5F4] text-[#042F2E] text-sm transition-all group"
                  >
                    <ExternalLink className="w-3.5 h-3.5 text-[#25a1c2] flex-shrink-0" />
                    <span className="truncate">{link.label}</span>
                  </Link>
                )
              )}
            </div>
          </div>

          <p className="text-center text-[10px] text-[#475569] italic mt-10">
            Bookmark this page → mindbodyspirithygienetools.com/social
          </p>
        </div>
      </div>
    </div>
  );
}
